import moment from "moment-timezone";
import React, { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  AlertSocket,
  AlertToken,
  IAlertMonitor,
  Reminder,
} from "../redux/alerts";
import { replayedAlertsAndActivities } from "../redux/alerts/selectors";
import { RepeatInclude } from "../redux/repeatAlert";
import { groupRepeatSettings } from "../redux/repeatAlert/selectors";
import { ResidentActivityType } from "../redux/residentActivityPlan";
import { IAppState } from "../redux/types";
import { alertTokenToSound } from "./AlertTo";
import SoundManager, { playNotificationWithRoom } from "./SoundManager";
import { usePrevious } from "./hooks/hooks";
export type playIndicators = {
  type: "Reminder" | "Alert";
  narrowType: AlertToken | ResidentActivityType;
  residentId: string;
  userId: string;
};
const SoundRepeat = (): JSX.Element => {
  const { byGroups, alerts, activities } = useSelector(
    replayedAlertsAndActivities
  );
  const prevAlers: any[] = usePrevious(alerts);
  const prevActivities: any[] = usePrevious(activities);

  const { monitorNodeId, notifications } = useSelector(
    (state: IAppState) => state.userProfile
  );
  const prevMonitor = usePrevious(monitorNodeId);
  const prevNotif = usePrevious(notifications);
  useEffect(() => {
    if (prevNotif?.sound === true && notifications?.sound === false)
      SoundManager.instance.stop();
    if (prevNotif?.push === true && notifications?.push === false)
      SoundManager.instance.stop();
  }, [prevNotif, notifications]);

  useEffect(() => {
    if (prevMonitor !== undefined && prevMonitor !== monitorNodeId) {
      SoundManager.instance.stop();
    }
  }, [prevMonitor, monitorNodeId]);

  useEffect(() => {
    if (prevAlers !== undefined && alerts.length !== prevAlers.length) {
      // found removed alert
      const removed = prevAlers.filter((x) => !alerts.includes(x));
      removed.map((el: AlertSocket | Reminder | IAlertMonitor) =>
        SoundManager.instance.clearNotification(
          el,
          "Alert",
          (el as IAlertMonitor).alertToken || (el as any).id,
          (el as IAlertMonitor).patientId,
          (el as IAlertMonitor)?.user?.id
        )
      );
    }
  }, [prevAlers, alerts]);
  useEffect(() => {
    if (
      prevActivities !== undefined &&
      prevActivities.length !== activities.length
    ) {
      // found removed alert
      const removed = prevActivities.filter((x) => !activities.includes(x));
      removed.map((el: AlertSocket | Reminder | IAlertMonitor) =>
        SoundManager.instance.clearNotification(
          el,
          "Reminder",
          (el as Reminder).activityType,
          (el as Reminder)?.residentId,
          ""
        )
      );
    }
  }, [prevActivities, activities]);
  return (
    <>
      {Object.keys(byGroups).map((nodeId) => {
        const { alerts, activities } = byGroups[nodeId];
        return (
          <OneGroupReplayedNotifications
            groupId={parseInt(nodeId)}
            alerts={alerts}
            activities={activities}
            key={nodeId}
          />
        );
      })}
    </>
  );
};

const OneGroupReplayedNotifications = ({
  groupId,
  alerts,
  activities,
}: {
  groupId: number;
  alerts: any[];
  activities: any[];
}): JSX.Element => {
  const groupSettings = useSelector((state: IAppState) =>
    groupRepeatSettings(state, groupId)
  );
  const prevSettings: any = usePrevious(groupSettings);
  const { language, notifications } = useSelector(
    (state: IAppState) => state.userProfile
  );

  useEffect(() => {
    if (prevSettings !== undefined && prevSettings !== groupSettings) {
      SoundManager.instance.stop();
    }
  }, [prevSettings, groupSettings]);
  return (
    <>
      {groupSettings &&
        groupSettings?.notificationRepeatIntervalInSeconds !== 0 &&
        alerts.map((el: IAlertMonitor) => (
          <OneweidNotificationSoundRepeat
            reminder={el}
            key={
              el.patientId +
              el.alertToken +
              language +
              el.lastUpdate +
              groupSettings?.notificationRepeatIntervalInSeconds +
              notifications?.sound +
              notifications?.push +
              +groupSettings?.notificationRepeatCount +
              JSON.stringify(groupSettings?.notificationMessageInclude) +
              el?.user?.id
            }
            language={language}
            repeat={notifications?.sound && notifications?.push}
            repeatTime={groupSettings?.notificationRepeatIntervalInSeconds}
            countRepeat={groupSettings?.notificationRepeatCount}
            includes={groupSettings?.notificationMessageInclude}
            playKeys={{
              type: "Alert",
              narrowType: el.alertToken || (el as any).id,
              residentId: el.patientId,
              userId: (el as IAlertMonitor)?.user?.id,
            }}
          />
        ))}
      {groupSettings &&
        groupSettings?.notificationRepeatIntervalInSeconds !== 0 &&
        activities.map((el: Reminder) => (
          <OneweidNotificationSoundRepeat
            reminder={el}
            key={
              el.activityType +
              el?.residentId +
              language +
              el.lastUpdate +
              groupSettings?.notificationRepeatIntervalInSeconds +
              groupSettings?.notificationRepeatCount +
              JSON.stringify(groupSettings?.notificationMessageInclude) +
              notifications?.sound +
              notifications?.push
            }
            language={language}
            repeat={notifications?.sound && notifications?.push}
            repeatTime={groupSettings?.notificationRepeatIntervalInSeconds}
            countRepeat={groupSettings?.notificationRepeatCount}
            includes={groupSettings?.notificationMessageInclude}
            playKeys={{
              type: "Reminder",
              narrowType: el.activityType,
              residentId: el?.residentId,
              userId: "",
            }}
          />
        ))}
    </>
  );
};

const OneweidNotificationSoundRepeat = ({
  reminder,
  language,
  repeatTime,
  repeat = false,
  countRepeat,
  includes,
  playKeys,
}: {
  reminder: Reminder | IAlertMonitor;
  language: string;
  repeatTime: number;
  repeat?: boolean;
  countRepeat: number;
  includes: RepeatInclude[];
  playKeys: playIndicators;
}): JSX.Element => {
  const whenStartFirstRepeat = useCallback(() => {
    const now = moment();
    const local = moment(reminder.lastUpdate);
    const diffTime = moment(now).diff(local);
    if (diffTime < 0) return 0;

    const durationInMSec = moment
      .duration(diffTime, "milliseconds")
      .asMilliseconds();
    const repeatTimeMs = repeatTime * 1000;
    const beforeRepeat =
      repeatTimeMs -
      (durationInMSec -
        Math.floor(durationInMSec / repeatTimeMs) * repeatTimeMs);
    return beforeRepeat;
  }, [reminder, repeatTime]);

  const playSoundNotification = useCallback(() => {
    const soundName = getSoundName(reminder);
    if (soundName) {
      playNotificationWithRoom(
        reminder,
        soundName,
        language,
        countRepeat,
        includes,
        playKeys.type,
        playKeys.narrowType,
        playKeys?.residentId,
        playKeys?.userId
      );
    }
  }, [reminder, language, countRepeat, includes, playKeys]);

  useEffect(() => {
    if (repeat) {
      const firstRepeat = whenStartFirstRepeat();

      let timeoutNext;
      let firstTimeout;
      if (firstRepeat !== 0) {
        firstTimeout = setTimeout(() => {
          clearTimeout(firstTimeout);
          playSoundNotification();
          timeoutNext = setInterval(() => {
            playSoundNotification();
          }, repeatTime * 1000);
        }, firstRepeat);
      } else {
        timeoutNext = setInterval(() => {
          playSoundNotification();
        }, repeatTime * 1000);
      }

      return () => {
        clearInterval(timeoutNext);
        if (firstTimeout) clearTimeout(firstTimeout);
      };
    }
  }, [repeat, repeatTime, whenStartFirstRepeat, playSoundNotification]);

  return <></>;
};

const getSoundName = (
  reminder: AlertSocket | Reminder | IAlertMonitor
): string => {
  return reminder["activityType"]
    ? reminder["activityType"] === ResidentActivityType.DRINKING
      ? reminder["activityType"] + "Reminder.mp3"
      : reminder["activityType"] + ".mp3"
    : alertTokenToSound((reminder as IAlertMonitor)?.alertToken);
};
// const OneNotificationSoundRepeat = ({
//     reminder,
//     language,
//     repeatTime,
//     repeat = false,
//     countRepeat,
// }: {
//     reminder: Reminder | IAlertMonitor;
//     language: string;
//     repeatTime: number;
//     repeat?: boolean;
//     countRepeat: number;
// }): JSX.Element => {
//     const [repeatLeft, setrepeatLeft] = useState(countRepeat);
//     console.log('recreate, repeat' + repeatLeft);
//     const whenStartFirstRepeat = useCallback(() => {
//         const now = moment();
//         const local = moment(reminder.lastUpdate);
//         // console.log(now.toISOString());
//         // console.log(local.toISOString());
//         const diffTime = moment(now).diff(local);
//         // console.log(diffTime);
//         if (diffTime < 0) return 0;

//         const durationInMSec = moment.duration(diffTime, 'milliseconds').asMilliseconds();
//         const repeatTimeMs = repeatTime * 1000;
//         const beforeRepeat = repeatTimeMs - (durationInMSec - Math.floor(durationInMSec / repeatTimeMs) * repeatTimeMs);
//         return beforeRepeat;
//     }, [reminder, repeatTime]);

//     const playSoundNotification = useCallback(() => {
//         const soundName = reminder['activityType']
//             ? reminder['activityType'] === ResidentActivityType.DRINKING
//                 ? reminder['activityType'] + 'Reminder.mp3'
//                 : reminder['activityType'] + '.mp3'
//             : alertTokenToSound((reminder as IAlertMonitor)?.self?.id);
//         if (soundName) {
//             playNotificationWithRoom(reminder, soundName, language, undefined);
//             setrepeatLeft(repeatLeft - 1);
//         }
//     }, [repeatLeft, reminder, language]);

//     useEffect(() => {
//         console.log('repeatLeft', repeatLeft);
//         if (repeat && repeatLeft > 0) {
//             const firstRepeat = whenStartFirstRepeat();

//             let timeoutNext;
//             let firstTimeout;
//             if (firstRepeat !== 0) {
//                 console.log('sound repeat enabled, first repeat in ' + firstRepeat + ' msseconds');
//                 firstTimeout = setTimeout(() => {
//                     clearTimeout(firstTimeout);
//                     playSoundNotification();
//                     timeoutNext = setInterval(() => {
//                         playSoundNotification();
//                         console.log('play in interval', repeatLeft);
//                     }, repeatTime * 1000);
//                 }, firstRepeat);
//             } else {
//                 console.log('less than a second');
//                 timeoutNext = setInterval(() => {
//                     playSoundNotification();
//                 }, repeatTime * 1000);
//             }

//             return () => {
//                 console.log('clearing repeat');
//                 clearInterval(timeoutNext);
//                 if (firstTimeout) clearTimeout(firstTimeout);
//             };
//         }
//     }, [repeat, repeatTime, repeatLeft]);

//     return <></>;
// };
export default React.memo(SoundRepeat);
